/**
 * useAutoProcessor - Automatic schedule execution hook
 * 
 * This hook provides client-side automatic processing of scheduled tasks.
 * It serves as a backup to the server-side cron job and ensures schedules
 * are executed even if the user has the app open.
 * 
 * Features:
 * - Processes due schedules every 60 seconds when app is visible
 * - Re-processes when user returns to the tab
 * - Shows toast notifications for executed schedules
 * - Does not interfere with server-side cron processing
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { triggerScheduleProcessor } from '../utils/supabase';
import { 
  executeCollectionAssignment, 
  executeCollectionRemoval,
  logActivity 
} from '../utils/schedule-api';
import { getSessionTokenForBackend, getShopDomain } from '../utils/authenticated-fetch';

interface AutoProcessorResult {
  lastProcessed: Date | null;
  isProcessing: boolean;
  processedCount: number;
  manualProcess: () => Promise<void>;
}

interface ToastMessage {
  title: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

const PROCESS_INTERVAL = 60 * 1000; // 1 minute

export function useAutoProcessor(
  onToast?: (toast: ToastMessage) => void,
  refreshSchedules?: () => Promise<void>
): AutoProcessorResult {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isProcessingRef = useRef(false);
  const hasStartedRef = useRef(false);
  const [lastProcessed, setLastProcessed] = useState<Date | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);

  // Store callbacks in refs to avoid dependency changes
  const onToastRef = useRef(onToast);
  const refreshSchedulesRef = useRef(refreshSchedules);
  
  useEffect(() => {
    onToastRef.current = onToast;
    refreshSchedulesRef.current = refreshSchedules;
  }, [onToast, refreshSchedules]);

  const processDueSchedules = useCallback(async (): Promise<void> => {
    const shopDomain = getShopDomain() || import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || '';
    if (isProcessingRef.current || !shopDomain) return;

    isProcessingRef.current = true;
    setIsProcessing(true);
    const now = new Date().toISOString();
    let processed = 0;

    try {
      // Fetch due assignments
      const { data: dueAssignments, error: assignmentError } = await supabase
        .from('scheduled_collection_assignments')
        .select('*')
        .eq('shop_domain', shopDomain)
        .eq('status', 'pending')
        .lte('scheduled_at', now)
        .order('scheduled_at', { ascending: true })
        .limit(10);

      if (assignmentError) {
        console.error('[AutoProcessor] Error fetching assignments:', assignmentError);
      } else if (dueAssignments && dueAssignments.length > 0) {
        console.log(`[AutoProcessor] Found ${dueAssignments.length} due assignments`);
        
        for (const assignment of dueAssignments) {
          try {
            await executeCollectionAssignment(assignment.id);
            processed++;
            console.log(`[AutoProcessor] ✅ Executed assignment: ${assignment.product_title} → ${assignment.collection_title}`);
          } catch (error) {
            console.error(`[AutoProcessor] ❌ Failed assignment ${assignment.id}:`, error);
          }
        }
      }

      // Fetch due removals
      const { data: dueRemovals, error: removalError } = await supabase
        .from('scheduled_collection_removals')
        .select('*')
        .eq('shop_domain', shopDomain)
        .eq('status', 'pending')
        .lte('scheduled_at', now)
        .order('scheduled_at', { ascending: true })
        .limit(10);

      if (removalError) {
        console.error('[AutoProcessor] Error fetching removals:', removalError);
      } else if (dueRemovals && dueRemovals.length > 0) {
        console.log(`[AutoProcessor] Found ${dueRemovals.length} due removals`);
        
        for (const removal of dueRemovals) {
          try {
            await executeCollectionRemoval(removal.id);
            processed++;
            console.log(`[AutoProcessor] ✅ Executed removal: ${removal.product_title} ← ${removal.collection_title}`);
          } catch (error) {
            console.error(`[AutoProcessor] ❌ Failed removal ${removal.id}:`, error);
          }
        }
      }

      // Section/theme schedules are processed by the server function. Check them
      // independently so an Asset Scheduler job does not require a collection job
      // to be due in the same minute.
      try {
        const sessionToken = await getSessionTokenForBackend();
        const shopDomainHeader = getShopDomain() || import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || '';
        const { data: hasDueResult, error: hasDueError } = await supabase.functions.invoke('app-api', {
          headers: {
            ...(shopDomainHeader ? { 'x-shop-domain': shopDomainHeader } : {}),
            ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
          },
          body: {
            action: 'hasDueSectionSchedules',
            data: {},
          },
        });

        if (hasDueError) {
          console.warn('[AutoProcessor] Failed to check section schedules:', hasDueError);
        } else if (hasDueResult?.hasDue) {
          console.log('[AutoProcessor] Detected pending section schedules due now - triggering server processor');
          const result = await triggerScheduleProcessor();
          if (result.success) {
            processed += Number(result.processed || 0);
          } else {
            const message = result.error || 'Server processor reported a failed scheduled action';
            console.warn('[AutoProcessor] Server processor reported failure:', result);
            if (onToastRef.current) {
              onToastRef.current({
                title: 'Schedule Processing Failed',
                message,
                type: 'error',
              });
            }
          }
        }
      } catch (err) {
        console.warn('[AutoProcessor] Could not process section schedules:', err);
        if (onToastRef.current) {
          onToastRef.current({
            title: 'Schedule Processing Failed',
            message: err instanceof Error ? err.message : 'Failed to trigger server processor',
            type: 'error',
          });
        }
      }

      // Update state and notify
      if (processed > 0) {
        setProcessedCount(prev => prev + processed);
        
        if (onToastRef.current) {
          onToastRef.current({
            title: 'Schedules Executed',
            message: `Automatically executed ${processed} scheduled action${processed !== 1 ? 's' : ''}`,
            type: 'success',
          });
        }

        // Refresh the schedule list
        if (refreshSchedulesRef.current) {
          await refreshSchedulesRef.current();
        }

        // If any section schedules (revert) are pending and due, trigger server processor as a fallback
        try {
          const sessionToken = await getSessionTokenForBackend();
          const shopDomainHeader = getShopDomain() || import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || '';
          const { data: hasDueResult, error: hasDueError } = await supabase.functions.invoke('app-api', {
            headers: {
              ...(shopDomainHeader ? { 'x-shop-domain': shopDomainHeader } : {}),
              ...(sessionToken ? { 'x-shopify-session-token': sessionToken } : {}),
            },
            body: {
              action: 'hasDueSectionSchedules',
              data: {},
            },
          });

          if (!hasDueError && hasDueResult?.hasDue) {
            // Trigger the server-side Edge Function to process section schedules
            console.log('[AutoProcessor] ⚠️ Detected pending section schedules due now - triggering server processor');
            try {
              await triggerScheduleProcessor();
            } catch (err) {
              console.warn('[AutoProcessor] Failed to trigger server processor:', err);
            }
          }
        } catch (err) {
          console.warn('[AutoProcessor] Could not check section schedules:', err);
        }

        // Log the auto-processing
        await logActivity('auto_processor_run', 'system', undefined, {
          processed_count: processed,
          timestamp: new Date().toISOString(),
        });
      }

      setLastProcessed(new Date());
    } catch (error) {
      console.error('[AutoProcessor] Error during processing:', error);
    } finally {
      isProcessingRef.current = false;
      setIsProcessing(false);
    }
  }, []); // No dependencies - uses refs for callbacks

  // Set up interval processing - runs once on mount
  useEffect(() => {
    const shopDomain = getShopDomain() || import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || '';
    if (!shopDomain || hasStartedRef.current) return;
    
    hasStartedRef.current = true;
    console.log(`[AutoProcessor] Started for ${shopDomain} - checking every ${PROCESS_INTERVAL / 1000}s`);

    // Process immediately on mount
    processDueSchedules();

    // Set up interval
    intervalRef.current = setInterval(processDueSchedules, PROCESS_INTERVAL);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        hasStartedRef.current = false;
        console.log('[AutoProcessor] Stopped');
      }
    };
  }, [processDueSchedules]);

  // Process when page becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      const shopDomain = getShopDomain() || import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN || '';
      if (document.visibilityState === 'visible' && shopDomain && !isProcessingRef.current) {
        processDueSchedules();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [processDueSchedules]);

  return {
    lastProcessed,
    isProcessing,
    processedCount,
    manualProcess: processDueSchedules,
  };
}
